import { Fragment, useEffect, useState } from 'react';
import {
  Box,
  Chip,
  Divider,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Tooltip,
  Typography,
  useMediaQuery
} from '@mui/material';
import { useTheme } from '@mui/system';
import { useDispatch } from 'react-redux';
import { useSelector } from 'store';
import { useLocation } from 'react-router';

import useChat from 'hooks/useChat';
import useAuth from 'hooks/useAuth';

import Avatar from 'components/@extended/Avatar';
import Dot from 'components/@extended/Dot';

import VidyalayaLogo from 'assets/images/vid_lms_logo.png';
import { UserAdd } from 'iconsax-react';

import {
  removeUnreadCount,
  setAiUnreadCount,
  setChatLoading,
  setChatMessage,
  setChatSkip,
  setCurrentUser,
  setTotalChatCount,
  setUnreadChip,
  setUserChatId,
  setUserRoomId,
  setUserSearch,
  setUserSwitching
} from 'store/reducers/chat';

import { Image } from 'types/auth';

// Import avatars dynamically
const avatarImage = (require as any).context('assets/images/users', true);

interface User {
  _id: string;
  firstName: string;
  lastName: string;
  type: string;
  isLoggedIn: boolean;
  userProfilePicId: Image;
  isAIChat?: boolean;
  roomId?: string;
}

interface AIUser {
  isAIChat: boolean;
  roomId: string;
}

interface RoomUser {
  users: User[];
  roomId: string;
  isAIChat: boolean;
}

function UserList({ closeDrawer }: Readonly<{ closeDrawer: () => void }>) {
  const theme = useTheme();
  const matchDownLG = useMediaQuery(theme.breakpoints.down('lg'));
  const dispatch = useDispatch();
  const { state } = useLocation();

  const { user } = useAuth();
  const { roomUsers, orgUsers, userChats, getRoomChats, skip, searchText, createRoom, activeUser } = useChat();

  const currentRoomId = useSelector((state) => state.chat.roomId);
  const AiChatId = useSelector((state) => state.chat.aiRoomId);
  const typing = useSelector((state) => state.chat.isTyping);

  const [data, setData] = useState<User[][]>([]);

  /* ---------------- existing chats ---------------- */
  useEffect(() => {
    const filtered =
      roomUsers?.map((room: RoomUser) => {
        if (room.isAIChat) return [{ roomId: room.roomId, isAIChat: true } as any];
        return room.users.filter((u) => u._id !== user?._id);
      }) || [];

    setData(filtered);
  }, [roomUsers, user?._id, activeUser]);

  /* ---------------- open chat ---------------- */
  const handleUserClick = async (roomId: string, chatUser: User | AIUser, count?: number) => {
    dispatch(setChatSkip(1));
    dispatch(setUserChatId(null));
    dispatch(setChatMessage(''));
    dispatch(setUserSwitching(true));

    await getRoomChats(roomId, 1);

    if (count && count > 0) {
      dispatch(removeUnreadCount(roomId));
    }

    dispatch(setUnreadChip(true));
    dispatch(setUserRoomId(roomId));
    dispatch(setCurrentUser(chatUser));

    (matchDownLG || location.pathname !== '/learner/chat') && closeDrawer();
  };

  /* ---------------- create room ---------------- */
  const handleCreateRoom = async (receiverUserId: string) => {
    const users = [receiverUserId, user?._id as string];
    await createRoom(users as [string]);
    dispatch(setUserSearch(''));
  };

  /* ---------------- pagination sync ---------------- */
  useEffect(() => {
    if (currentRoomId && skip !== 1) {
      getRoomChats(currentRoomId);
    } else if (skip !== 1) {
      dispatch(setTotalChatCount(userChats.length - 1));
      dispatch(setChatLoading(false));
    }
  }, [skip]);

  /* ---------------- AI auto-open ---------------- */
  useEffect(() => {
    if (AiChatId && state?.type === 'aibot') {
      const room = roomUsers.find((r: any) => r.roomId === AiChatId);
      handleUserClick(AiChatId, { roomId: AiChatId, isAIChat: true }, room?.unreadChatCount);
      dispatch(setAiUnreadCount(room?.unreadChatCount));
    }
  }, [AiChatId]);

  /* ================================================= */
  return (
    <List component="nav">
      {/* ================= EXISTING ROOMS ================= */}
      {searchText === ''
        ? data.map((users, index) => (
            <Fragment key={index}>
              <ListItemButton
                sx={{ pl: 1 }}
                onClick={() => {
                  const room = roomUsers.find((r: any) => r.users.includes(users[0]));
                  if (users[0]?.isAIChat) {
                    handleUserClick(AiChatId, { roomId: AiChatId, isAIChat: true }, roomUsers[index]?.unreadChatCount);
                  } else if (room) {
                    handleUserClick(room.roomId, users[0], roomUsers[index]?.unreadChatCount);
                  }
                }}
              >
                <ListItemAvatar>
                  <Avatar
                    alt={users[0]?.firstName}
                    src={
                      users[0]?.isAIChat
                        ? VidyalayaLogo
                        : users[0]?.userProfilePicId?.path
                        ? `${process.env.REACT_APP_FILE_URL}${users[0].userProfilePicId.path.replace(/\\/g, '/')}`
                        : avatarImage
                    }
                    size="md"
                  />
                </ListItemAvatar>

                <ListItemText
                  primary={
                    <Typography variant="subtitle1">
                      {users[0]?.isAIChat ? 'Sapphire Bot' : `${users[0].firstName} ${users[0].lastName}`}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography variant="subtitle2">
                        {roomUsers[index]?.lastMessage?.file?.length > 0 ? '📎 File Attachment' : roomUsers[index]?.lastMessage?.text || ''}
                      </Typography>
                      {typing?.[users[0]?._id] && <Typography color="green">typing...</Typography>}
                    </>
                  }
                />
              </ListItemButton>
              <Divider />
            </Fragment>
          ))
        : /* ================= SEARCH USERS ================= */
          orgUsers.map((orgUser: any) => {
            if (orgUser._id === user?._id) return null;

            const existingRoom = roomUsers.find((room: any) => room.users.some((u: any) => u._id === orgUser._id));

            return (
              <Fragment key={orgUser._id}>
                <ListItemButton
                  sx={{ pl: 1 }}
                  onClick={async () => {
                    if (existingRoom) {
                      handleUserClick(existingRoom.roomId, orgUser);
                    } else {
                      await handleCreateRoom(orgUser._id);
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Avatar
                      alt={orgUser.firstName}
                      src={
                        orgUser?.userProfilePicId?.path
                          ? `${process.env.REACT_APP_FILE_URL}${orgUser.userProfilePicId.path.replace(/\\/g, '/')}`
                          : avatarImage
                      }
                      size="md"
                    />
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Stack direction="row" justifyContent="space-between" alignItems="center">
                        <Typography variant="subtitle1">
                          {orgUser.firstName} {orgUser.lastName}
                        </Typography>

                        {!existingRoom && (
                          <IconButton
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCreateRoom(orgUser._id);
                            }}
                          >
                            <UserAdd size="26" />
                          </IconButton>
                        )}
                      </Stack>
                    }
                  />
                </ListItemButton>
                <Divider />
              </Fragment>
            );
          })}
    </List>
  );
}

export default UserList;
